import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Button, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import LoaderIcon from '../../components/loaderIcon/LoaderIcon';
import { IModifierDetails, IModifierSetDetails, IOrderDetailItems } from '../home/models/orderDetailModels';
import { getFormattedPrice } from '../../components/helper/common';
import orderDetailStyles from './OrderDetail.module.scss';
import Footer from '../../components/footer/Footer';
import usePreAuthScripts from '../../hooks/usePreAuthScripts';
import ViewTicketDetailPageContext, {
	IViewTicketDetailPageContext,
} from '../../Contexts/ViewTicketPageContext/ViewTicketPageContext';
import AppContext, { IAppContext } from '../../Contexts/AppContext/AppContext';
import { constructPlaceOrderRequestBody } from '../../utils/orderUtils';
import OrderDetailPageContext, {
	IOrderDetailContext,
} from '../../Contexts/OrderDetailPageContext/OrderDetailPageContext';
import * as labels from './labels';
import ErrorModal from '../../components/errormodal/ErrorModal';
import ApiErrorModal from '../../components/errormodal/ApiErrorModal';
import { getInvalidCardError } from '../preAuthenticationDetail/labels';
import PreAuthPageContext, { IPreAuthItemContext } from '../../Contexts/PreAuthPageContext/PreAuthPageContext';
import MenuItemsPageContext, { IMenuItemContext } from '../../Contexts/MenuItemsPageContext/MenuItemsPageContext';
import OrderConfirmPageContext, {
	IOrderConfirmContext,
} from '../../Contexts/OrderConfirmPageContext/OrderConfirmPageContext';
import { IViewTabItem } from '../viewTickets/models/ticketDetailsModels';
import { useTranslation } from '../../hooks/useTranslation';
import { RouteConfig } from '../../routeConfig/routeConfig';




const OrderDetailPreAuth: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const isOpenTabView = location.pathname.includes('viewOpenTab');
	const tableRefId = localStorage.getItem('tableRef');
	const { merchantData } = useContext(MenuItemsPageContext) as IMenuItemContext;
	const {
		isValidSession,
		validSessionId,
		updateToastMessage,
		checkSessionOnLoad,
	} = useContext(AppContext) as IAppContext;
	const { getQRPayHost } = useContext(OrderConfirmPageContext) as IOrderConfirmContext;
	const { reCaptchaLoaded, generateReCaptchaToken } = usePreAuthScripts(true, false, t);
	const { ticketDetails, getTicket, ticketError, setTicketError, shouldPlaceOrder, setShouldPlaceOrder, isCheckSessionValid, setShouldRedirectToViewCheck, setTicketDetails } =
		useContext(ViewTicketDetailPageContext) as IViewTicketDetailPageContext;
	const { postOrderDetail } = useContext(OrderDetailPageContext) as IOrderDetailContext;
	const { setRecaptchaToken, setIsActiveSession } = useContext(PreAuthPageContext) as IPreAuthItemContext;
	const [loading, setLoading] = useState<boolean>(false);
	const [payView, setPayView] = useState<boolean>(false);
	const [viewTabDetails, setviewTabDetails] = useState<IViewTabItem[]>([]);
	const [orderDetail, setOrderDetail] = useState({
		shopName: '',
		customerName: '',
		table: '',
		order: {
			items: [
				{
					menuItemGuid: '',
					specialRequest: '',
					itemName: '',
					itemPrice: '',
					quantity: 1,
					modSets: [],
				},
			],
			totalPrice: '',
		},
	});
	const [totalPriceInCart, setTotalPriceInCart] = useState<number>(0);
	const { storeId } = useParams<{ storeId: string }>();
	const order = localStorage.getItem('orderDetail');
	const existingSessionId = localStorage.getItem('storedSessionId');
	const isQrPayEnabled = localStorage.getItem('isQRPayEnabled') === 'true';
	const [apiErrorMessage, setApiErrorMessage] = useState({ MESSAGE: '', TITLE: '', STATUS: 0 });
	const [placeOrderError, setPlaceOrderError] = useState({ MESSAGE: '', TITLE: '' });

	useEffect(() => {
		window.scrollTo(0, 0);
		setShouldPlaceOrder(false);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (payView && ticketDetails?.amountPaid) {
			if (Number(ticketDetails?.amountPaid > 0)) {
				updateToastMessage(t('pages:viewTicket:error:partialPayment:message'));
				return;
			}
			if (isValidSession && validSessionId && storeId) {
				getQRPayHost(storeId, validSessionId);
			}
			setPayView(false);
			setLoading(false);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticketDetails, payView, isValidSession]);


	useEffect(() => {

		if (isOpenTabView) {
			if (ticketDetails && Array.isArray(ticketDetails.items)) {
				const mappedItems: IViewTabItem[] = (ticketDetails.items ?? []).map(item => ({
					guid: "",
					name: item.name ?? "",
					price: item.price ?? "",
					priceNoDiscount: "",
					quantity: item.quantity ?? "",
					modifiers: item.modifiers ?? "",
					discounts: [],
					specialRequests: item.specialRequests ?? [],
					isOpenItem: false,
					isReorderable: true,
					menuItemGuid: "",
				}));
				setviewTabDetails(mappedItems);
			}

		} else if (order) {
			setOrderDetail(JSON.parse(order));
			const { order: orderDetails } = JSON.parse(order);
			if (orderDetails?.totalPrice >= 0 && orderDetails?.items?.length > 0) {
				setTotalPriceInCart(Number(orderDetails?.totalPrice));
			}
		}

		if (!isCheckSessionValid && ticketError?.status) {
			setLoading(false);
			if ([404, 400, 500].includes(ticketError.status)) {
				setApiErrorMessage(labels.getPreAuthApiError(t, ticketError.status));
				return;
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCheckSessionValid, order, ticketError, isOpenTabView]);


	useEffect(() => {
		if (isOpenTabView && existingSessionId && storeId) {
			getTicket(storeId, existingSessionId, false);
			return;
		}
		if (shouldPlaceOrder && isCheckSessionValid) {
			placeOrder();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldPlaceOrder, isCheckSessionValid, isOpenTabView]);

	useEffect(() => {
		if (orderDetail?.order?.items?.length === 0) {
			localStorage.removeItem('orderDetail');
			localStorage.removeItem('cartItems');
			localStorage.removeItem('ticketRefDetail');
			navigate(`/${storeId}?tableRef=${tableRefId}`);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderDetail]);

	const handleBackClick = () => {
		navigate(`/${storeId}?tableRef=${tableRefId}`);
	};

	const modifiersDom = (modifiers: IModifierDetails, modifiersDomIndex: number) => {
		return (
			<div key={modifiersDomIndex}>
				{modifiers.menuModGuid && modifiers.menuModName && (
					<div className={orderDetailStyles.ticketRow}>
						<div className={orderDetailStyles.modifiersDomLabel}>{modifiers.menuModName}</div>
						<div className={orderDetailStyles.modifiersTicketRowValue}>
							{Number(modifiers.modifierPrice) !== 0 &&
								merchantData &&
								`${getFormattedPrice(Number(modifiers.modifierPrice), merchantData?.currency)}`}
						</div>
					</div>
				)}
				{modifiers.modSets &&
					modifiers.modSets?.length >= 0 &&
					modifiers.modSets.map((modifierSet3: IModifierSetDetails, modifierSet3Index: number) => {
						return modifierSetDom(modifierSet3, modifierSet3Index);
					})}
			</div>
		);
	};

	const modifierSetDom = (modifierSet: IModifierSetDetails, modifierSetIndex: number) => {
		return (
			<div className={orderDetailStyles.modifierSetDomContainer} key={modifierSetIndex}>
				<div className={orderDetailStyles.ticketRow}>
					<div className={orderDetailStyles.modifierSetDomLabel}>{modifierSet.menuModSetName}</div>
				</div>

				{modifierSet?.mods &&
					modifierSet?.mods?.length >= 0 &&
					modifierSet?.mods.map((modifierSet2: IModifierDetails, modifiersDomIndex: number) => {
						return modifiersDom(modifierSet2, modifiersDomIndex);
					})}
			</div>
		);
	};

	const itemsRenderDom = (itemDetails: IOrderDetailItems | IViewTabItem, itemDetailIndex: number) => {
		const quantity = isOpenTabView
			? parseFloat(itemDetails.quantity?.toString() ?? '').toString()
			: itemDetails.quantity;

		const name = "itemName" in itemDetails
			? (itemDetails as IOrderDetailItems).itemName
			: (itemDetails as IViewTabItem).name;

		if (!merchantData?.currency) {
			return null;
		}

		const price = isOpenTabView
			? getFormattedPrice(Number((itemDetails as IViewTabItem).price) * 100, merchantData?.currency)
			: getFormattedPrice(Number((itemDetails as IOrderDetailItems).itemPrice), merchantData?.currency);

		const modifiersContent = "modSets" in itemDetails
			? itemDetails.modSets?.map((modSet, index) => modifierSetDom(modSet, index))
			: itemDetails.modifiers && itemDetails.modifiers.length > 0 && (
				<div className={orderDetailStyles.modifierSetDomContainer}>
					<div className={orderDetailStyles.ticketRow}>
						<div className={orderDetailStyles.modifierSetDomLabel}>
							{itemDetails.modifiers}
						</div>
					</div>
				</div>
			);

		return (
			<Fragment key={itemDetailIndex}>
				<div className={orderDetailStyles.ticketRowMain}>
					<div className={orderDetailStyles.ticketRowLabelBold}>
						<div className={orderDetailStyles.itemQuantity}>{quantity}</div>
						<div className={orderDetailStyles.itemName}>{name}</div>
					</div>
					{merchantData && (
						<div className={orderDetailStyles.ticketRowValue}>{price}</div>
					)}
				</div>

				{modifiersContent}

				{!isOpenTabView && (
					<div className={orderDetailStyles.removeIcon}>
						<Button
							className={orderDetailStyles.removeItmeButton}
							onClick={() => handleRemoveItem(itemDetails as IOrderDetailItems, itemDetailIndex)}
						>
							{t('page:orderDetails:remove:button')}
						</Button>
					</div>
				)}

				<div className={orderDetailStyles.divider} />
			</Fragment>
		);
	};



	const handleRemoveItem = (removeItem: IOrderDetailItems, itemDetailIndex: number) => {
		let modifierPrice = 0;
		const itemsInCart = localStorage.getItem('cartItems');
		const merchantName = localStorage.getItem('merchantName');
		const tableRef = localStorage.getItem('tableRef');

		if (itemsInCart) {
			let pendingItems = JSON.parse(itemsInCart);
			let updatedPrice = Number(pendingItems.totalPrice);
			const itemQuantity = Number(removeItem.quantity);
			let removedItemPrice = Number(removeItem.itemPrice);
			removedItemPrice = removedItemPrice * itemQuantity;
			if (pendingItems?.items[itemDetailIndex]?.menuItemGuid === removeItem.menuItemGuid) {
				pendingItems?.items?.splice(itemDetailIndex, 1);
			}
			pendingItems = pendingItems.items;
			const modLevel = (modLevelItem: IModifierDetails[]) => {
				modLevelItem.forEach((item2: IModifierDetails) => {
					modifierPrice = modifierPrice + Number(item2.modifierPrice) * itemQuantity;

					if (item2.modSets) {
						modsetLevel(item2.modSets);
					}
				});
			};
			const modsetLevel = (modsetLevelItem: IModifierSetDetails[]) => {
				modsetLevelItem.forEach((item1: IModifierSetDetails) => {
					if (item1.mods) {
						modLevel(item1.mods);
					}
				});
			};
			if (removeItem.modSets) {
				modsetLevel(removeItem.modSets);
			}
			updatedPrice = updatedPrice - removedItemPrice - modifierPrice;
			const updatedOrderDetail = JSON.stringify({
				order: { totalPrice: updatedPrice, items: pendingItems },
				shopName: merchantName,
				table: tableRef,
			});
			localStorage.setItem('orderDetail', updatedOrderDetail);
			localStorage.setItem('cartItems', JSON.stringify({ totalPrice: updatedPrice, items: pendingItems }));
			setOrderDetail(JSON.parse(updatedOrderDetail));

		} else {
			localStorage.removeItem('orderDetail');
			localStorage.removeItem('cartItems');
			navigate(`/${storeId}`);
		}
	};

	const startSession = async () => {
		await generateReCaptchaToken('order').then((recaptchaToken) => {
			if (recaptchaToken) {
				setRecaptchaToken(recaptchaToken);
				navigate(`/${storeId}/startSession`);
			}
		});
	};


	const handlePlaceOrder = async () => {
		if (!tableRefId) {
			localStorage.clear();
			navigate(RouteConfig.scanToStart)
			return;
		}
		if (existingSessionId && storeId) {
			const isSessionExpired = await checkSessionOnLoad(storeId, existingSessionId);
			if (isSessionExpired) {
				navigate(RouteConfig.scanToStart);
			}
		}
		if (existingSessionId && storeId) {
			setLoading(true);
			getTicket(storeId, existingSessionId, false);
		} else {
			startSession();
		}
	};

	const handleOrderView = () => {
		setShouldRedirectToViewCheck(false);
		setIsActiveSession(false);
		navigate(`/${storeId}?tableRef=${tableRefId}`);
	};

	const handlePayView = async () => {
		setLoading(true);
		setTicketDetails({});
		if (existingSessionId && storeId) {
			getTicket(storeId, existingSessionId, false);
			setPayView(true);
		}
	};

	const placeOrder = async () => {
		if (existingSessionId && storeId) {
			const isSessionExpired = await checkSessionOnLoad(storeId, existingSessionId);
			if (isSessionExpired) {
				navigate(RouteConfig.scanToStart);
			}
		}
		if (orderDetail && storeId) {
			const orderDetailRequestBody = constructPlaceOrderRequestBody(orderDetail);
			const orderDetailResponseObject = await postOrderDetail(storeId, orderDetailRequestBody);
			setShouldPlaceOrder(false);
			setLoading(false);
			if (orderDetailResponseObject?.error && [500, 400, 404].includes(orderDetailResponseObject.error.status)) {
				localStorage.removeItem('storedSessionId');
				if (orderDetailResponseObject.error.status === 500) {
					setApiErrorMessage(labels.getPreAuthApiError(t, 500));
				} else {
					setPlaceOrderError(getInvalidCardError(t));
				}
			} else {
				const selectedPreviousTicketRef = localStorage.getItem('selectedPreviousTicketRef');
				if (!selectedPreviousTicketRef && orderDetailResponseObject.order?.ticketGuid) {
					localStorage.setItem('selectedPreviousTicketRef', orderDetailResponseObject.order.ticketGuid);
				}
				localStorage.removeItem('cartItems');
				navigate(`/${storeId}/OrderConfirmView`);
			}
		}
	};

	const handleErrorClick = () => {
		setApiErrorMessage({ MESSAGE: '', TITLE: '', STATUS: 0 });
		if (isOpenTabView) {
			setTicketError(null);
			navigate(`/${storeId}/homePage`);
			return;
		}
		if (ticketError?.status === 404 || ticketError?.status === 400) {
			startSession();
			setTicketError(null);
		} else {
			setTicketError(null);
			navigate(`/${storeId}?tableRef=${tableRefId}`);
		}
	};

	const handlePlaceOrderError = () => {
		setPlaceOrderError({ MESSAGE: '', TITLE: '' });
		navigate(`/${storeId}?tableRef=${tableRefId}`);
	};


	if (placeOrderError.MESSAGE?.length > 0) {
		return (
			<ApiErrorModal
				handleDismiss={handlePlaceOrderError}
				typeOfError={placeOrderError.TITLE}
				errorMsg={placeOrderError.MESSAGE}
				actionType={t('component:apiErrorModal:retry:button')}
				isBackIconEnabled={true}
			/>
		);
	}
	return (
		<>
			{apiErrorMessage.MESSAGE?.length > 0 ? (
				<ErrorModal
					handleDismiss={handleErrorClick}
					typeOfError={apiErrorMessage.TITLE}
					errorMsg={apiErrorMessage.MESSAGE}
					showButton={true}
				/>
			) : (
				<div className={orderDetailStyles.orderDetailContainer} id="orderDetailContainer">
					<div className={orderDetailStyles.orderDetailHeading}>
						<div className={orderDetailStyles.headingTitle}> {isOpenTabView ? t('page:orderDetails:openTab:heading') : t('page:orderDetails:reviewOrder:heading')}</div>
						<Button variant="text" className={orderDetailStyles.backButton} onClick={handleBackClick}>
							<CloseIcon color="action" />
						</Button>
					</div>
					<div className={orderDetailStyles.ticketContainer}>
						<div className={orderDetailStyles.ticketRow}>
							<InfoIcon className={orderDetailStyles.infoIcon} />
							<Typography className={orderDetailStyles.infoText}>
								{isOpenTabView ? t('page:orderDetails:orderedItemsPreparation:label') : t('page:orderDetails:itemsSelectedPrepared:label')}
							</Typography>
						</div>
					</div>
					<div 
						className={`${orderDetailStyles.detailContainer}
						${isOpenTabView ? orderDetailStyles.marginBottom : ''}
					`}>
						<div className={orderDetailStyles.itemDetailsContainer}>
							{isOpenTabView
								? viewTabDetails?.map((itemDetail, index) => itemsRenderDom(itemDetail, index))
								: orderDetail?.order?.items?.map((itemDetail, index) => itemsRenderDom(itemDetail, index))
							}
						</div>
					</div>
					<div className={orderDetailStyles.footer}>
						{isOpenTabView ? (
							<div className={orderDetailStyles.orderActions}>
								<Button
									id="orderMoreBtn"
									color="primary"
									fullWidth
									className={`${orderDetailStyles.orderMoreBtn} ${orderDetailStyles.btn} fluid`}
									onClick={handleOrderView}
								>
									{t('page:orderDetails:orderMore:button')}
								</Button>
								{isQrPayEnabled && (<Button
									id="payCheckButton"
									color="secondary"
									fullWidth
									className={`${orderDetailStyles.payBtn} ${orderDetailStyles.btn}`}
									onClick={handlePayView}
								>
									{loading && <LoaderIcon />}
									{t('page:orderDetails:pay:button')}
								</Button>)}
							</div>
						) : (
							<Footer
								id={existingSessionId ? 'Place Order' : 'Continue'}
								externalStyle={orderDetailStyles.placeOrderBackground}
								labelText={existingSessionId ? t('page:orderDetails:placeOrder:button') : t('page:orderDetails:startTab:button')}
								itemPrice={totalPriceInCart}
								showQuantityDetail={false}
								addToOrderInnerWithoutParams={handlePlaceOrder}
								loaderStatus={!reCaptchaLoaded || loading}
							/>
						)}
					</div>

				</div>
			)}
		</>
	);
};
export default OrderDetailPreAuth;
